import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import find from 'lodash/find';
import toNumber from 'lodash/toNumber';
import isUndefined from 'lodash/isUndefined';
import uniqueId from 'lodash/uniqueId';
import {
  API,
  ToastActions,
  Field2 as Field,
  SidePanelContainer,
  SidePanelContent,
  SidePanelFooter,
  SidePanelSection,
  TableLocationZoneManagement,
  VibeModal,
  VibeButton,
  VibeIcon,
  viArchive,
  viUnarchive,
  color,
} from 'vibeguide';

/**
 * Reset the confirmation modal dialog
 */
function resetConfirmModal() {
  return {
    show: false,
    title: '',
    description: '',
    confirmColor: '',
    onConfirm: () => {},
    onCancel: () => {},
  };
}

let confirmationCode = null;
let options = {};

function CompanyBannerEdit({
  className = '',
  style = {},
  companyId,
  bannerId = null,
  user,
  queueToast,
}) {
  const [banner, setBanner] = useState({});
  const [multiplierOption, setMultiplierOption] = useState(null);
  const [confirmModal, setConfirmModal] = useState(resetConfirmModal());
  const [confirmMultiplierModal, setConfirmMultiplierModal] = useState(false);
  const [locationZones, setLocationZones] = useState([]);
  const [tableLocationZones, setTableLocationZones] = useState([]);
  const [ready, setReady] = useState(bannerId === null);

  const showArchive = banner._id && user.can('company_banner.delete');
  const isArchived = banner._id && !banner.active;

  const disableSave = !banner.name;
  const disableInput = (banner._id && !user.can('company_banner.modify'))
    || (!banner._id && !user.can('company_banner.create'));

  /**
   * Cancel the confirmation modal
   */
  const onCancelConfirmModal = () => {
    setConfirmModal(resetConfirmModal());
  };

  /**
   * User confirms archiving the banner
   */
  const onArchiveBanner = async () => {
    onCancelConfirmModal();

    await API.Company.Banner.deactivate({
      _id: banner._id,
      companyId,
    });

    setBanner({
      ...banner,
      active: false,
    });

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveCompanyBanner'));
  };

  /**
   * User clicks Archive
   */
  const onClickArchive = () => {
    setConfirmModal({
      show: true,
      title: 'Archive Banner',
      description: `Are you sure you want to archive ${get(banner, 'name', 'Unknown')}?`,
      confirmColor: 'error',
      onConfirm: onArchiveBanner,
      onCancel: onCancelConfirmModal,
    });
  };

  /**
   * User clicks Unarchive
   */
  const onClickUnarchive = async () => {
    await API.Company.Banner.reactivate({
      _id: banner._id,
      companyId,
    });

    setBanner({
      ...banner,
      active: true,
    });

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveCompanyBanner'));
  };

  /**
   * When the user cancels the update to the banner (when applicable)
   */
  const onCancelUpdateBanner = () => {
    // reset the multiplier option
    setMultiplierOption(null);
    setConfirmMultiplierModal(false);
  };

  /**
   * When the user clicks the cancel update confirmation modal
   * This also resets the confirmation workflow by resetting the confirmationCode and options headers
   */
  const onClickCancelUpdateBanner = () => {
    onCancelUpdateBanner();

    // reset the confirmation code / post options
    confirmationCode = null;
    options = {};
  };

  /**
   * When the user confirms the update to the banner (when applicable)
   */
  const onConfirmUpdateBanner = async () => {
    onCancelUpdateBanner();
    await onSave();

    // reset the confirmation code / post options (needed after onSave to maintain confirmationCode and header options)
    confirmationCode = null;
    options = {};
  };

  /**
   * User changes the input box
   */
  const onChange = ({
    target: {
      name,
      value,
    },
  }) => {
    setBanner({
      ...banner,
      [name]: value,
    });
  };

  /**
   * When zones selection changes in the table
   */
  const onChangeSelectedZones = (rows) => {
    setBanner({
      ...banner,
      // strip out any undefined rows
      locationZones: rows.filter(row => row !== undefined).map(row => {
        return {
          _id: row._id,
          name: row.name,
          audioMultiplier: toNumber(row.audioMultiplier) || 0,
          visualMultiplier: toNumber(row.visualMultiplier) || 0,
        };
      }),
    });
  };

  /**
   * When row data is changed in the zone assignments
   */
  const onChangeRowData = (rows) => {
    // banner has location zones assigned to it, update the zone data
    if (banner.locationZones.length > 0) {
      setBanner({
        ...banner,
        locationZones: banner.locationZones.map(item => {
          // find the new row that has been potentially updated, if none is found, use the existing row data
          const row = find(rows, { _id: item._id }) || item;

          return {
            _id: row._id,
            name: row.name,
            audioMultiplier: toNumber(row.audioMultiplier) || 0,
            visualMultiplier: toNumber(row.visualMultiplier) || 0,
          };
        }),
      });
    }
  };

  /**
   * Save the banner
   */
  const onSave = async () => {
    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    if (confirmationCode) {
      options = { headers: { 'VAPI-ConfirmationCode': confirmationCode } };
    }

    const data = {
      companyId,
      name: banner.name,
      locationZones: get(banner, 'locationZones', []).map(zone => {
        return {
          ...zone,
          audioMultiplier: toNumber(zone.audioMultiplier) / 100,
          visualMultiplier: toNumber(zone.visualMultiplier) / 100,
        };
      }),
    };

    if (banner._id) {
      data._id = banner._id;
    }

    if (multiplierOption) {
      data.multiplierApplyMode = multiplierOption;
    }

    try {
      const response = banner._id
        ? await API.Company.Banner.update(data, options)
        : await API.Company.Banner.create(data, options);

      const responseType = get(response, '[0].type');
      const confirmationRequired = !isUndefined(get(response, '[0].vapiConfirmationCode'));
      const success = banner._id
        ? responseType === 'COMPANYBANNER.UPDATED'
        : responseType === 'COMPANYBANNER.CREATED';

      if (confirmationRequired) {
        confirmationCode = get(response, '[0].vapiConfirmationCode');
        setConfirmMultiplierModal(true);
        return;
      }

      if (success) {
        // Successfully saved the banner
        const saveMessage = banner._id
          ? 'Banner Updated!'
          : 'Banner Created!';

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
          delay: 500,
        });
      }

      document.dispatchEvent(new Event('onSaveCompanyBanner'));
    } catch (err) {
      console.error('Error saving banner', err);

      queueToast({
        type: 'error',
        title: 'Error Saving Banner',
        timeout: 10,
        allowClose: true,
      });

      document.dispatchEvent(new Event('onSaveCompanyBannerError'));
    }
  };

  /**
   * Get the banner details
   * Also fetch the global list of location zones that can be applied to the banner
   * Data is merged from the assigned location zones on the banner with the global list of zones
   */
  useEffect(() => {
    if (user.can('location-zone.view')) {
      async function fetchData() {
        const allLocationZones = await API.LocationZones.list({
          pageSize: -1,
        });

        const locationZonesList = get(allLocationZones, 'data', []) || [];

        setLocationZones(locationZonesList.map(locationZone => {
          return {
            // local collections need a row ID before hitting the table for row selection to work properly
            _rowId: uniqueId(`${locationZone._id}-`),
            ...locationZone,
          };
        }));
      }
      fetchData();
    }

    if (bannerId) {
      async function fetchData() {
        const response = await API.Company.Banner.get({
          _id: bannerId,
          companyId,
        });

        setBanner({
          ...response,
          locationZones: (get(response, 'locationZones', []) || []).map(zone => {
            return {
              ...zone,
              // Round down so results don't come up as 14.000000002
              audioMultiplier: Math.floor((toNumber(zone.audioMultiplier) * 100)) || 0,
              visualMultiplier: Math.floor((toNumber(zone.visualMultiplier) * 100)) || 0,
            };
          }),
        });
      }
      fetchData();

    }

    setReady(true);
  }, []);

  /**
   * When banner location zone data is changed, re-render the zone assignment table
   */
  useEffect(() => {
    setTableLocationZones(locationZones.map(locationZone => {
      // check if the banner has this location zone assigned
      const bannerLocationZone = find(banner.locationZones, { _id: locationZone._id });

      // use the banner location zone with multiplier information if it is assigned
      if (bannerLocationZone) {
        return {
          ...locationZone,
          ...bannerLocationZone,
        };
      }

      return locationZone;
    }));
  }, [banner.locationZones, locationZones]);

  if (!ready) {
    return null;
  }

  return (
    <SidePanelContainer
      className={classNames('CompanyBannerEdit', className)}
      style={style}
    >
      <SidePanelContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        <SidePanelSection
          title="Banner Information"
          style={{
            padding: 24,
            paddingBottom: 0,
          }}
        >
          <Field
            style={{
              width: 320,
            }}
            type="text"
            label="Banner"
            placeholder="Banner"
            name="name"
            value={banner.name}
            tabIndex={1}
            disabled={disableInput}
            testId="company-banner-name-input"
            onChange={onChange}
            required
            autoFocus
          />
        </SidePanelSection>

        {user.can('location-zone.view') && (
          <SidePanelSection
            title="Zone Assignments"
            titleStyle={{
              padding: '0 24px',
            }}
            style={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              marginBottom: 0,
            }}
          >
            <TableLocationZoneManagement
              columns={[
                '.',
                'Zone',
                'Audio Impression Multiplier',
                'Visual Impression Multiplier',
                '...',
              ]}
              collection={tableLocationZones}
              defaultSortBy={{
                label: 'Zone',
                attr: 'name',
                direction: 'asc',
              }}
              paginator
              paginatorProps={{
                label: 'Zones',
                urlPaging: false,
                urlFilters: false,
              }}
              bulk
              bulkProps={{
                edit: false,
                add: false,
                block: false,
                remove: false,
                archive: false,
              }}
              selectedIds={(banner.locationZones || []).map(zone => zone._id)}
              disableSelect={disableInput}
              editMode={!disableInput}
              onChangeSelected={onChangeSelectedZones}
              onChange={onChangeRowData}
            />
          </SidePanelSection>
        )}
      </SidePanelContent>

      <SidePanelFooter>
        <VibeButton
          text="Save Changes"
          color="secondary"
          disabled={disableSave || disableInput}
          testId="company-banner-save-button"
          onClick={onSave}
        />

        {banner._id && (
          <div>
            {showArchive && !isArchived && (
              <VibeIcon
                icon={viArchive}
                type="button"
                buttonProps={{
                  size: 32,
                  borderColor: color.error,
                  style: {
                    marginLeft: 8,
                  },
                }}
                tooltip="Archive"
                color={color.error}
                size={20}
                data-testid="company-banner-archive-button"
                onClick={onClickArchive}
              />
            )}

            {showArchive && isArchived && (
              <VibeIcon
                icon={viUnarchive}
                type="button"
                buttonProps={{
                  size: 32,
                  borderColor: color.success,
                  style: {
                    marginLeft: 8,
                  },
                }}
                tooltip="Unarchive"
                color={color.success}
                size={20}
                data-testid="company-banner-unarchive-button"
                onClick={onClickUnarchive}
              />
            )}
          </div>
        )}
      </SidePanelFooter>

      <VibeModal
        type="confirm"
        show={confirmModal.show}
        confirmProps={{
          color: confirmModal.confirmColor,
        }}
        title={confirmModal.title}
        text={confirmModal.description}
        testIds={{
          root: 'company-banner-confirm-dialog',
          confirm: 'company-banner-confirm-button',
          cancel: 'company-banner-cancel-button',
        }}
        onConfirm={confirmModal.onConfirm}
        onClose={confirmModal.onCancel}
      />

      <VibeModal
        type="confirm"
        show={confirmMultiplierModal}
        confirmProps={{
          disabled: !multiplierOption,
        }}
        title="Update Impression Multipliers"
        text={(
          <div
            style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
          >
            <Field
              type="radio"
              label="Apply to future locations only"
              radioProps={{
                selected: multiplierOption === 'replace_none',
              }}
              testId="company-banner-multiplier-replace-none-option"
              onClick={() => setMultiplierOption('replace_none')}
            />

            <Field
              type="radio"
              label="Update values only where they match"
              radioProps={{
                selected: multiplierOption === 'replace_same',
              }}
              testId="company-banner-multiplier-replace-same-option"
              onClick={() => setMultiplierOption('replace_same')}
            />

            <Field
              type="radio"
              label="Update all location multipliers"
              radioProps={{
                selected: multiplierOption === 'replace_all',
              }}
              testId="company-banner-multiplier-replace-all-option"
              onClick={() => setMultiplierOption('replace_all')}
            />
          </div>
        )}
        testIds={{
          root: 'company-banner-multiplier-dialog',
          confirm: 'company-banner-multiplier-confirm-button',
          cancel: 'company-banner-multiplier-cancel-button',
        }}
        onConfirm={onConfirmUpdateBanner}
        onClose={onClickCancelUpdateBanner}
      />
    </SidePanelContainer>
  );
}

CompanyBannerEdit.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  companyId: PropTypes.string.isRequired,
  bannerId: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyBannerEdit);
